import React from 'react';
import { CircularProgress } from '../CircularProgress';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';

/**
 * Props for the PulseButton component
 */
export interface PulseButtonProps {
  /**
   * The style variant of the button
   * @default 'solid'
   */
  variant?: 'solid' | 'plain' | 'soft' | 'outline';

  /**
   * The color variant of the button
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'neutral' | 'danger' | 'success';

  /**
   * The size of the button
   * @default 'md'
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';

  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * FontAwesome icon to display at the start of the button
   */
  startDecorator?: IconDefinition;

  /**
   * FontAwesome icon to display at the end of the button
   */
  endDecorator?: IconDefinition;

  /**
   * Click handler for the button
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;

  /**
   * Button content
   */
  children?: React.ReactNode;

  /**
   * HTML button type
   * @default 'button'
   */
  type?: 'button' | 'submit' | 'reset';

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Test ID for testing and analytics
   */
  'data-testid'?: string;

  /**
   * Accessible label for the button (required for screen readers when button has no text)
   */
  'aria-label'?: string;

  /**
   * Whether the controlled element is expanded
   */
  'aria-expanded'?: boolean;

  /**
   * Indicates the type of popup element
   */
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';

  /**
   * Whether the button is pressed/active (for toggle buttons)
   */
  'aria-pressed'?: boolean;

  /**
   * Whether the button is in a loading state
   * @default false
   */
  loading?: boolean;
}

/**
 * PulseButton - A versatile button component with multiple variants and sizes
 *
 * @example
 * ```tsx
 * <PulseButton color="primary" size="md" onClick={handleClick}>
 *   Click Me
 * </PulseButton>
 * ```
 *
 * @example
 * ```tsx
 * import { faTrash } from '@fortawesome/pro-solid-svg-icons';
 *
 * <PulseButton
 *   color="danger"
 *   size="lg"
 *   startDecorator={faTrash}
 *   disabled
 * >
 *   Delete
 * </PulseButton>
 * ```
 */
const PulseButton: React.FC<PulseButtonProps> = ({
  variant = 'solid',
  color = 'primary',
  size = 'md',
  disabled = false,
  startDecorator,
  endDecorator,
  onClick,
  children,
  type = 'button',
  className = '',
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  'aria-haspopup': ariaHaspopup,
  'aria-pressed': ariaPressed,
  loading = false,
}): React.ReactElement => {
  /**
   * Get background color classes based on variant, color, and state
   */
  const getBackgroundClasses = (): string => {
    if (variant === 'solid') {
      const solidColorMap = {
        primary: {
          default: 'bg-sky-600',
          hover: 'hover:bg-sky-800',
          disabled: 'bg-sky-50',
        },
        secondary: {
          default: 'bg-teal-600',
          hover: 'hover:bg-teal-700',
          disabled: 'bg-teal-50',
        },
        neutral: {
          default: 'bg-neutral-500',
          hover: 'hover:bg-neutral-600',
          disabled: 'bg-neutral-100',
        },
        danger: {
          default: 'bg-red-500',
          hover: 'hover:bg-red-600',
          disabled: 'bg-red-50',
        },
        success: {
          default: 'bg-green-700',
          hover: 'hover:bg-green-800',
          disabled: 'bg-green-50',
        },
      };

      const colors = solidColorMap[color];
      return disabled ? colors.disabled : `${colors.default} ${colors.hover}`;
    }

    if (variant === 'soft') {
      // Soft variant - disabled state
      if (disabled) {
        return 'bg-neutral-50';
      }

      const softColorMap = {
        primary: {
          default: 'bg-sky-50',
          hover: 'hover:bg-sky-100',
        },
        secondary: {
          default: 'bg-teal-50',
          hover: 'hover:bg-teal-100',
        },
        neutral: {
          default: 'bg-neutral-100',
          hover: 'hover:bg-neutral-200',
        },
        danger: {
          default: 'bg-red-100',
          hover: 'hover:bg-red-200',
        },
        success: {
          default: 'bg-green-100',
          hover: 'hover:bg-green-200',
        },
      };

      const colors = softColorMap[color];
      return `${colors.default} ${colors.hover}`;
    }

    if (variant === 'outline') {
      // Outline variant - disabled state
      if (disabled) {
        return 'bg-transparent';
      }

      const outlineColorMap = {
        primary: 'hover:bg-sky-50',
        secondary: 'hover:bg-teal-50',
        neutral: 'hover:bg-neutral-50',
        danger: 'hover:bg-red-50',
        success: 'hover:bg-green-50',
      };

      return `bg-transparent ${outlineColorMap[color]}`;
    }

    // Plain variant
    if (disabled) {
      return 'bg-transparent';
    }

    const plainColorMap = {
      primary: 'hover:bg-sky-50',
      secondary: 'hover:bg-teal-50',
      neutral: 'hover:bg-neutral-200',
      danger: 'hover:bg-red-50',
      success: 'hover:bg-green-50',
    };

    return `bg-transparent ${plainColorMap[color]}`;
  };

  /**
   * Get text color classes based on variant, color, and state
   */
  const getTextClasses = (): string => {
    if (variant === 'solid') {
      if (!disabled) {
        return 'text-white';
      }

      const solidDisabledTextMap = {
        primary: 'text-sky-100',
        secondary: 'text-teal-100',
        neutral: 'text-neutral-300',
        danger: 'text-red-200',
        success: 'text-green-200',
      };

      return solidDisabledTextMap[color];
    }

    if (variant === 'soft') {
      // Soft variant - disabled state
      if (disabled) {
        return 'text-neutral-300';
      }

      const softTextMap = {
        primary: 'text-sky-900',
        secondary: 'text-teal-700',
        neutral: 'text-primary',
        danger: 'text-red-500',
        success: 'text-green-700',
      };

      return softTextMap[color];
    }

    if (variant === 'outline') {
      // Outline variant - disabled state
      if (disabled) {
        const outlineDisabledTextMap = {
          primary: 'text-sky-100',
          secondary: 'text-teal-100',
          neutral: 'text-neutral-200',
          danger: 'text-red-50',
          success: 'text-green-200',
        };

        return outlineDisabledTextMap[color];
      }

      const outlineTextMap = {
        primary: 'text-sky-900',
        secondary: 'text-teal-500',
        neutral: 'text-primary',
        danger: 'text-red-500',
        success: 'text-green-700',
      };

      return outlineTextMap[color];
    }

    // Plain variant
    if (disabled) {
      const plainDisabledTextMap = {
        primary: 'text-sky-100',
        secondary: 'text-teal-100',
        neutral: 'text-neutral-300',
        danger: 'text-red-200',
        success: 'text-green-200',
      };

      return plainDisabledTextMap[color];
    }

    const plainTextMap = {
      primary: 'text-sky-900',
      secondary: 'text-teal-700',
      neutral: 'text-primary',
      danger: 'text-red-500',
      success: 'text-green-700',
    };

    return plainTextMap[color];
  };

  /**
   * Get size classes for height, padding and text
   * Heights match PulseIconButton for consistent alignment
   */
  const getSizeClasses = (): string => {
    const sizeMap = {
      xs: 'h-6 text-xs px-2',
      sm: 'h-8 text-xs px-2.5',
      md: 'h-10 text-sm px-3.5',
      lg: 'h-12 text-base px-4',
    };

    return sizeMap[size];
  };

  /**
   * Get gap class for spacing between decorators and content
   */
  const getGapClass = (): string => {
    const gapMap = {
      xs: 'gap-1',
      sm: 'gap-1',
      md: 'gap-2',
      lg: 'gap-2',
    };

    return gapMap[size];
  };

  /**
   * Get border classes for outline variant
   */
  const getBorderClasses = (): string => {
    if (variant !== 'outline') {
      return '';
    }

    if (disabled) {
      const outlineDisabledBorderMap = {
        primary: 'border border-sky-100',
        secondary: 'border border-teal-100',
        neutral: 'border border-neutral-200',
        danger: 'border border-red-50',
        success: 'border border-green-200',
      };

      return outlineDisabledBorderMap[color];
    }

    const outlineBorderMap = {
      primary: 'border border-sky-600',
      secondary: 'border border-teal-500',
      neutral: 'border border-neutral-200',
      danger: 'border border-red-500',
      success: 'border border-green-700',
    };

    return outlineBorderMap[color];
  };

  const baseClasses =
    'relative inline-flex items-center justify-center w-fit font-bold rounded-3xl transition-colors duration-200 focus:outline-none';
  const backgroundClasses = getBackgroundClasses();
  const textClasses = getTextClasses();
  const sizeClasses = getSizeClasses();
  const gapClass = getGapClass();
  const borderClasses = getBorderClasses();
  const isDisabled = disabled || loading;
  const disabledClasses = isDisabled ? 'cursor-not-allowed' : 'cursor-pointer';

  /**
   * Check if icon is a spinner to add animation
   */
  const isSpinnerIcon = (icon: IconDefinition): boolean => {
    return icon.iconName === 'spinner';
  };

  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      aria-pressed={ariaPressed}
      className={twMerge(
        baseClasses,
        backgroundClasses,
        textClasses,
        sizeClasses,
        gapClass,
        borderClasses,
        disabledClasses,
        className
      )}
      data-testid={dataTestId}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <CircularProgress size="sm" />
        </span>
      )}
      <span className={`inline-flex items-center gap-[inherit] ${loading ? 'invisible' : ''}`}>
        {startDecorator && (
          <span className="flex items-center justify-center">
            <Icon
              icon={startDecorator}
              className={isSpinnerIcon(startDecorator) ? 'animate-spin' : ''}
            />
          </span>
        )}
        {children}
      </span>
      {endDecorator && (
        <span className={`flex items-center justify-center ${loading ? 'invisible' : ''}`}>
          <Icon
            icon={endDecorator}
            className={isSpinnerIcon(endDecorator) ? 'animate-spin' : ''}
          />
        </span>
      )}
    </button>
  );
};

export default PulseButton;
